import { IAccount, INavigation } from '@share/common-types';

/**
 * Resolves whether the redesigned My Account hub is enabled for the current navigation org/site.
 * Same rules as `resolveDisplayCertificatesForNavigation`: preference on, then RSI-template tenants
 * are narrowed by the org/site allowlists (both empty = every org/site).
 * Called from `getNavigationMenu`; the result is merged onto `account.displayAccountHub`.
 */
export function resolveDisplayAccountHubForNavigation(
  account: IAccount | null | undefined,
  items: INavigation | null | undefined,
): boolean {
  // Capital Vacations (members and agents) keep the legacy My Account; nothing in the hub applies to them.
  if (account?.isCapitalVacations) {
    return false;
  }

  // Local development only: forces the hub on while no tenant has the preference yet.
  // `DEV` is false in every `vite build`, so this can never reach a deployed bundle.
  if (import.meta.env.DEV && import.meta.env.VITE_FORCE_ACCOUNT_HUB === 'true') {
    return !!account;
  }

  if (!account?.displayAccountHub) {
    return false;
  }

  if (!account.isRSITemplate) {
    return true;
  }

  const siteIdList = (account.displayAccountHubSiteidList || []).filter(Boolean);
  const orgIdList = (account.displayAccountHubOrgidList || []).filter(Boolean);
  if (!siteIdList.length && !orgIdList.length) {
    return true;
  }

  const siteId = items?.siteId?.toString() ?? '';
  const orgId = items?.organizationId?.toString() ?? '';
  return siteIdList.includes(siteId) || orgIdList.includes(orgId);
}

/** Use after the navigation menu has merged the resolved `displayAccountHub` onto the account. */
export function isDisplayAccountHub(account: IAccount | null | undefined): boolean {
  return !!account?.displayAccountHub && !account?.isCapitalVacations;
}
